// Permanent image URLs. The API hands out S3 links signed for an hour (X-Amz-Expires=3600), so an og:image, a
// JSON-LD photo or an <img> Google indexed broke an hour later. The site serves the same object at
// /media/<s3 key> instead (app/media/[...key]/route.ts): a URL that never changes and is cached at the edge.

const SITE_ORIGIN = 'https://milacelebrations.com'; // lib/metadata.ts CANONICAL_SITE_ORIGIN (which imports this file)

// Mila's upload buckets (next.config.js images.remotePatterns): <bucket>.s3.<region>.amazonaws.com/<key>?X-Amz-…
const S3_OBJECT = /^https:\/\/(mila-[a-z0-9-]+)\.s3\.[a-z0-9-]+\.amazonaws\.com\/([^?#]+)/i;

/** The S3 key of one of Mila's upload links (signed or not), or null for anything else. */
export function s3Key(url: string | null | undefined): string | null {
  const m = String(url || '').match(S3_OBJECT);
  if (!m) return null;
  let key: string;
  try { key = decodeURIComponent(m[2]); } catch { key = m[2]; }
  return key && !key.split('/').includes('..') ? key : null;
}

// Folders of public catalogue images (what the API's public endpoints return). Anything else — documents, receipts,
// chat files — never gets a /media URL and the route refuses it.
const PUBLIC_FOLDERS = ['blog', 'categories', 'items', 'packages', 'physical-spaces', 'vendors', 'venue-occasions', 'venues'];

/** A key /media may serve: a file inside a public image folder, no empty or dot segments. */
export function isPublicMediaKey(key: string): boolean {
  const parts = key.split('/');
  return parts.length >= 2 && PUBLIC_FOLDERS.includes(parts[0]) && parts.every((s) => s && s !== '.' && s !== '..')
    && /\.(jpe?g|png|webp|gif|avif)$/i.test(key); // raster only: an uploaded SVG could carry script on our origin
}

const encodeKey = (key: string) => key.split('/').map(encodeURIComponent).join('/');

// On unless NEXT_PUBLIC_MEDIA_PROXY=off (the backend's GET /media is live; the switch keeps the signed links for a
// deploy that must run against a backend without it).
const enabled = () => process.env.NEXT_PUBLIC_MEDIA_PROXY !== 'off';

/** Site-relative permanent URL of an image (/media/<key>); anything that is not a public Mila upload is returned as is. */
export function mediaPath<T extends string | null | undefined>(url: T): T | string {
  if (!enabled()) return url;
  const key = s3Key(url);
  return key && isPublicMediaKey(key) ? `/media/${encodeKey(key)}` : url;
}

/** A copy of an API payload with every public Mila image link swapped for its /media path (strings deep inside
 *  objects and arrays included); everything else is left as it is. */
export function permanentImages<T>(value: T): T {
  if (typeof value === 'string') return mediaPath(value) as T;
  if (Array.isArray(value)) return value.map(permanentImages) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = permanentImages(v);
    return out as T;
  }
  return value;
}

/** Article HTML with the src of every public Mila image swapped for its /media path (blog bodies embed S3 links). */
export function permanentImagesInHtml(html: string | null | undefined): string {
  if (!html) return html ?? '';
  return html.replace(/(\ssrc=["'])(https:\/\/mila-[a-z0-9-]+\.s3\.[a-z0-9-]+\.amazonaws\.com\/[^"']+)(["'])/gi,
    (all, open: string, url: string, close: string) => {
      const p = mediaPath(url.replace(/&amp;/g, '&'));
      return p.startsWith('/media/') ? `${open}${p}${close}` : all;
    });
}

// The site's own origin (NEXT_PUBLIC_BASE_URL, e.g. https://milacelebrations.com), for the image optimizer below.
const siteBase = () => (process.env.NEXT_PUBLIC_BASE_URL || '').replace(/\/+$/, '');

/** src + unoptimized for next/image. Our public files (/v9) and Mila's upload buckets (next.config.js remotePatterns)
 *  are resized. A /media path is a route handler, not a file in public/, and the hosting's optimizer only reads files
 *  for a site-relative src (/_next/image?url=/media/… is a 404), so it gets the absolute URL instead; without
 *  NEXT_PUBLIC_BASE_URL the /media image is served as is. Anything else is not resized. */
export function imageProps(src: string): { src: string; unoptimized: boolean } {
  if (src.startsWith('/media/')) {
    const base = siteBase();
    return base ? { src: `${base}${src}`, unoptimized: false } : { src, unoptimized: true };
  }
  const optimizable = src.startsWith('/') || /^https:\/\/mila-[a-z0-9-]+\.s3\.[a-z0-9-]+\.amazonaws\.com\//i.test(src);
  return { src, unoptimized: !optimizable };
}

/** Absolute permanent URL (og:image, JSON-LD); site-relative paths are made absolute too. */
export function mediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const p = mediaPath(url);
  return p.startsWith('/') ? `${SITE_ORIGIN}${p}` : p;
}
